import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Plane, ArrowRight, Check, Clock } from "lucide-react";

export default function WelcomePilot() {
  const { user } = useAuth();
  const first = (user?.name || "").trim().split(" ")[0] || "pilot";

  useEffect(() => {
    if (typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", { content_name: "Welcome Founding Pilot" });
    }
  }, []);

  return (
    <div className="min-h-screen pb-16" data-testid="welcome-pilot-page">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-8 md:p-10 rounded-sm border border-primary/40 bg-primary/5"
        >
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-primary mb-4">
            <Plane className="h-4 w-4" /> Cleared for takeoff
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="welcome-pilot-heading">
            Welcome aboard, {first}.
          </h1>
          <p className="text-base text-slate-300 leading-relaxed mb-3">
            Congratulations — your free account is ready.
            Over the next five short lessons you'll build the mindset every professional pilot starts with and answer one question:
            <strong className="text-slate-100"> can we build the best drone training academy on the internet?</strong>
          </p>
          <p className="text-base text-slate-300 leading-relaxed mb-7">
            When you finish, we'll ask for your honest opinion. Not compliments — your honest opinion. It shapes every
            lesson that comes next.
          </p>

          <div className="rounded-sm border border-border/60 bg-secondary/30 p-5 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Your first mission — about 9 minutes</span>
            </div>
            <ul className="space-y-2">
              {[
                "Learn how professional drone pilots think",
                "Understand the mindset behind safe flying",
                "Complete your first mission",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-200">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={3} /> {t}
                </li>
              ))}
            </ul>
          </div>

          <Link to="/module/gs-welcome" data-testid="welcome-start-lesson">
            <Button className="h-12 px-7 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold text-base group w-full sm:w-auto">
              Start Your First Lesson
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <div className="mt-4">
            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary" data-testid="welcome-dashboard-link">
              Skip to my dashboard
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
